import React from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  AppBar,
  Box,
  Toolbar,
  Modal,
  Grid,
  Button,
  Typography,
  makeStyles,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import LowestPrice from "components/LowestPrice";
import CloseIcon from "@material-ui/icons/Close";
import molliesLogoRed from "images/Mollies_Ico_Red.svg";
import { AvailableUnit } from "../AvailableUnit/AvailableUnit";
import { formatFaIcon } from "utils/helpers";
import ImageCarousel from "components/ImageCarousel";

const useStyles = makeStyles((theme) => ({
  appBar: {
    position: "sticky",
    top: "0",
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
  },
  toolbar: {
    justifyContent: "space-between",
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingLeft: "6%",
    paddingRight: "6%",
    minHeight: "inherit",
  },
  roomDetailsModalContent: {
    height: "100%",
    width: "100%",
    overflow: "scroll",
    position: "absolute",
    backgroundColor: theme.palette.background.default,
    boxShadow: theme.shadows[5],
  },
  galleryContainer: {
    overflow: "hidden",
    position: "relative",
    maxHeight: "60%",
  },
  closeButton: {
    color: theme.palette.text.primary,
  },
  img: {
    objectFit: "cover",
    height: "100%",
    width: "100%",
  },
  imgOverlay: {
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    position: "absolute",
    pointerEvents: "none",
  },
  roomContainer: {
    position: "relative",
    margin: "30px 1rem 0 1rem",
    justifyContent: "left",
    [theme.breakpoints.up("desktop")]: {
      marginLeft: "0",
      marginRight: "0",
      justifyContent: "space-evenly",
      paddingBottom: "200px",
    },
    color: theme.palette.text.primary,
  },
  descriptionCol: {
    [theme.breakpoints.up("desktop")]: {
      width: "52%",
    },
  },
  descriptionRow: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  roomDescription: {
    whiteSpace: "pre-wrap",
  },
  packagesCol: {
    [theme.breakpoints.up("desktop")]: {
      width: "20%",
    },
  },
  packageRow: {
    marginBottom: "30px",
    padding: "25px",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    color: theme.palette.text.primary,
  },
  selectAvailCta: {
    maxWidth: "120px",
    marginTop: "25px",
  },
  detailIcon: {
    fontSize: "20px",
    textAlign: "center",
    marginTop: "6px",
    marginBottom: theme.spacing(2),
  },
  amenity: {
    width: "50%",
  },
  amenityContent: {
    border: `1px solid ${theme.palette.divider}`,
    width: "100%",
    padding: theme.spacing(2),
  },
  roomTitle: {
    position: "absolute",
    bottom: theme.spacing(2),
    left: theme.spacing(2),
    right: theme.spacing(2),
    color: theme.palette.text.onDark.primary,
  },
  detailTitle: {
    marginBottom: "30px",
  },
  redText: {
    color: theme.palette.text.red,
    fontWeight: 700,
  },
  navbarLogo: {
    width: "150px",
    [theme.breakpoints.up("tablet")]: {
      width: "180px",
    },
  },
  infoIcon: {
    color: theme.palette.text.primary,
    fontSize: "1.3rem",
    marginLeft: "10px",
    cursor: "pointer",
  },
}));

const RoomDetailsModal = (props) => {
  const classes = useStyles();

  const theme = useTheme();
  const tabletUpScreen = useMediaQuery(theme.breakpoints.up("tablet"));

  return props.roomDetails ? (
    <Modal open={props.open} onClose={props.onClose}>
      <Grid container className={classes.roomDetailsModalContent}>
        <AppBar className={classes.appBar}>
          <Toolbar className={classes.toolbar}>
            <img className={classes.navbarLogo} src={molliesLogoRed} alt="Mollies Logo" />
            <Button className={classes.closeButton} onClick={props.onClose} endIcon={<CloseIcon />}>
              {tabletUpScreen ? "Close" : ""}
            </Button>
          </Toolbar>
        </AppBar>
        <Grid container className={classes.galleryContainer}>
          <ImageCarousel
            images={props.roomDetails?.gallery?.map((g) => g.image).filter((i) => !!i)}
            isHeaderCarousel={true}
          />

          <div className={classes.imgOverlay} />
          <Typography variant="h2" className={classes.roomTitle}>
            {props.roomDetails.name}
          </Typography>
        </Grid>
        <Grid container className={classes.roomContainer}>
          <Grid item sm={6} className={classes.descriptionCol}>
            <LowestPrice
              availabilities={props.roomAvails}
              stayNights={props.stayNights}
              onDark={false}
            />
            <Grid className={classes.descriptionRow}>
              <Typography variant="body1" className={classes.roomDescription}>
                {props.roomDetails.description}
              </Typography>
            </Grid>
            <Grid container direction="column" className={classes.descriptionRow}>
              <Grid container>
                <Typography variant="h5" className={classes.detailTitle}>
                  In every room
                </Typography>
              </Grid>
              <Grid container spacing={1}>
                {props.roomDetails.features.map((feature) => (
                  <Grid item container className={classes.amenity} key={feature.id}>
                    <Box className={classes.amenityContent}>
                      {feature.icon && (
                        <>
                          <FontAwesomeIcon
                            className={classes.detailIcon}
                            icon={formatFaIcon(feature.icon)}
                          />{" "}
                        </>
                      )}
                      <Grid container item xs={8} direction="column">
                        <Typography variant={tabletUpScreen ? "body1" : "body2"} gutterBottom>
                          {feature.title}
                        </Typography>
                      </Grid>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
          <Grid container direction="column" className={classes.packagesCol}>
            <Typography variant="h5" className={classes.detailTitle}>
              Select package
            </Typography>
            {props.roomAvails.map((availability, index) => (
              <AvailableUnit
                key={index}
                availability={availability}
                onReservationSelect={(e) => props.onReservationSelect(availability, e)}
                stayNights={props.stayNights}
                verticalMode
              />
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Modal>
  ) : null;
};

RoomDetailsModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  roomDetails: PropTypes.object,
  roomAvails: PropTypes.arrayOf(PropTypes.object),
  stayNights: PropTypes.number,
  onReservationSelect: PropTypes.func,
};

export default RoomDetailsModal;
